
import React from 'react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';

export interface SuccessStoryBentoCardProps {
  id: string;
  name: string;
  title: string;
  oneLiner: string;
  image: string;
  excerpt?: string;
  stats: {
    revenue?: string;
    employeeCount?: string;
    partners?: string;
    impact?: string;
  };
  size?: 'small' | 'medium' | 'large';
  className?: string;
  fullStory?: {
    story: string[];
  };
}

const SuccessStoryBentoCard: React.FC<SuccessStoryBentoCardProps> = ({
  id,
  name,
  title,
  oneLiner,
  image,
  excerpt,
  stats,
  size = 'medium',
  className,
  fullStory,
}) => {
  // Get a description from the full story or fallback to excerpt/oneLiner
  const getDescription = () => {
    if (fullStory?.story && fullStory.story.length > 0) {
      // Get first paragraph of the story and trim it
      const firstParagraph = fullStory.story[0];
      const maxLength = {
        small: 100,
        medium: 160,
        large: 220
      };
      
      // Trim to appropriate length for card size
      if (firstParagraph.length > maxLength[size]) {
        return firstParagraph.substring(0, maxLength[size]) + '...';
      }
      return firstParagraph;
    }
    
    // Fallback to excerpt or oneLiner
    return excerpt || oneLiner;
  };
  
  // Get number of lines to display based on card size and screen size
  const getDescriptionLines = () => {
    const sizeMap = {
      small: 'line-clamp-2 sm:line-clamp-3',
      medium: 'line-clamp-3 sm:line-clamp-4',
      large: 'line-clamp-4 sm:line-clamp-5'
    };
    return sizeMap[size];
  };
  
  return (
    <div 
      className={cn(
        "rounded-xl overflow-hidden transition-all duration-300 group h-full",
        "hover:shadow-lg relative bento-tilt",
        className
      )}
    >
      {/* Card content with dark overlay and text on top of the image */}
      <div className="flex flex-col h-full relative">
        {/* Full-sized image as background with dark overlay */}
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70 z-10"></div>
          <img 
            src={image} 
            alt={name}
            className="w-full h-full object-cover transition-transform duration-700 
                      group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = '/placeholder.svg';
            }}
          />
        </div>
        
        {/* Content overlay */}
        <div className="relative z-20 flex flex-col h-full p-4 sm:p-5 md:p-6 text-white">
          {/* Meta information - create more visual interest at top */}
          <div className="flex flex-wrap gap-2 mb-2 sm:mb-3">
            {stats.revenue && (
              <span className="text-xs bg-amber-400/80 text-black px-2 py-0.5 rounded-full font-medium
                            transition-all duration-300 group-hover:bg-amber-400">
                {stats.revenue}
              </span>
            )}
            
            {stats.impact && (
              <span className="text-xs bg-purple-400/80 text-black px-2 py-0.5 rounded-full font-medium
                            transition-all duration-300 group-hover:bg-purple-400">
                {stats.impact}
              </span>
            )}
          </div>
          
          {/* Title - adjusted for different screen sizes */}
          <h3 className={cn(
            "font-bold text-lg sm:text-xl md:text-2xl mb-1 sm:mb-2 md:mb-3",
            "text-shadow-sm group-hover:text-amber-100 transition-colors",
            size === 'large' && "lg:text-3xl"
          )}>
            {title}
          </h3>
          
          {/* Author name */}
          <p className="text-sm text-white/90 font-medium mb-1 sm:mb-2">{name}</p>
          
          {/* Description - responsive line clamping */}
          <p className={cn(
            "text-xs sm:text-sm md:text-base text-white/90 font-light",
            getDescriptionLines(),
            size === 'large' ? 'mb-3 sm:mb-4' : 'mb-2 sm:mb-3'
          )}>
            {getDescription()}
          </p>
          
          {/* Bottom stats/tags - always show on mobile, but more on larger cards */}
          <div className="mt-auto flex flex-wrap gap-2">
            {stats.employeeCount && (
              <span className="text-xs bg-blue-400/80 text-black px-2 py-0.5 rounded-full font-medium
                            transition-all duration-300 group-hover:bg-blue-400">
                {stats.employeeCount}
              </span>
            )}
            
            {stats.partners && (
              <span className="text-xs bg-green-400/80 text-black px-2 py-0.5 rounded-full font-medium
                            transition-all duration-300 group-hover:bg-green-400">
                {stats.partners}
              </span>
            )}
            
            {/* Read more text */}
            <span className="ml-auto text-amber-100 text-xs font-medium underline decoration-dotted">
              Read full story
            </span>
          </div>
          
          {/* Link overlay to make entire card clickable */}
          <Link 
            to={`/success-story/${id}`} 
            className="absolute inset-0 z-30" 
            aria-label={`Read more about ${name}'s story: ${title}`}
          />
        </div>
      </div>
    </div>
  );
};

export default SuccessStoryBentoCard;
